import React, { useState, useEffect, useRef } from "react";
import { useStore } from "./store";
import { SearchableCountrySelect } from "./site/formFields";
import { findCountry, flagOf } from "./site/countries";
import {
  Sparkles, Camera, Edit3, Save, X, Upload, Shield, CheckCircle2,
  LogOut, CreditCard, ShoppingBag, ArrowLeftRight, Bot, Users, PiggyBank,
  ChevronRight, User, Phone, Mail, Globe, Calendar, MessageCircle,
  FileText, AlertCircle, Lock, Bell
} from "lucide-react";

/* ═════════════════════════════════════════════════════════════════
   LIVE STAKING TICKER — animated earnings ticker for investor demo
   Shows: pension + insurance total growing in real-time at 8%/mo
═════════════════════════════════════════════════════════════════ */
export function LiveStakingTicker({ pensionTgc = 0, insuranceTgc = 0 }) {
  const total = pensionTgc + insuranceTgc;
  const dailyAccrual = total * (0.08 / 30); // 8% monthly → daily
  const perSecond = dailyAccrual / 86400;
  const perHour = perSecond * 3600;

  const [earned, setEarned] = useState(0);
  const mountTs = useRef(Date.now());

  useEffect(() => {
    mountTs.current = Date.now();
    setEarned(0);
    if (perSecond <= 0) return;
    const t = setInterval(() => {
      setEarned(((Date.now() - mountTs.current) / 1000) * perSecond);
    }, 80);
    return () => clearInterval(t);
  }, [perSecond]);

  if (total <= 0) return null;

  return (
    <div className="card mt-md" data-testid="live-staking-ticker"
      style={{
        background: "linear-gradient(135deg, #0D1B4B 0%, #1A3A8F 50%, #0E6B5E 100%)",
        color: "white", padding: 16, borderColor: "rgba(94,234,212,0.4)",
        borderWidth: 2, position: "relative", overflow: "hidden"
      }}>
      <div style={{ position: "absolute", top: 10, right: 10, display: "flex", alignItems: "center", gap: 4 }}>
        <div style={{ width: 8, height: 8, borderRadius: 4, background: "#10F4A7", animation: "pulse 1.2s infinite" }} />
        <span style={{ fontSize: 10, opacity: 0.85 }}>LIVE</span>
      </div>
      <div className="row gap-sm" style={{ marginBottom: 6 }}>
        <Sparkles size={16} color="#FFD566" />
        <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.9 }}>Стейкинг работает прямо сейчас</span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: -1, fontFeatureSettings: '"tnum"' }}>
        {(total + earned).toFixed(6)}
        <span style={{ fontSize: 14, opacity: 0.7, fontWeight: 600, marginLeft: 6 }}>TGC</span>
      </div>
      <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>
        Pension + Insurance · 8% / мес · капитализация ежемесячно
      </div>
      <div className="row between" style={{ marginTop: 10, fontSize: 11 }}>
        <span style={{ color: "#5EEAD4" }}>+{perHour.toFixed(4)} / час</span>
        <span style={{ color: "#FFD566" }}>+{dailyAccrual.toFixed(2)} / день</span>
        <span style={{ color: "#FFA3B0" }}>+{(dailyAccrual * 30).toFixed(1)} / мес</span>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   FULL PROFILE SCREEN — avatar upload, edit, KYC with docs
═════════════════════════════════════════════════════════════════ */
export function ProfileScreenV2({ onNav }) {
  const { user, logout, updateProfile } = useStore();
  const [editMode, setEditMode] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);
  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName:  user?.lastName  || "",
    phone:     user?.phone     || "",
    country:   user?.country   || "",
    dateOfBirth: user?.dateOfBirth || "",
    telegram:  user?.telegram  || "",
  });

  useEffect(() => {
    if (!editMode) {
      setForm({
        firstName: user?.firstName || "",
        lastName:  user?.lastName  || "",
        phone:     user?.phone     || "",
        country:   user?.country   || "",
        dateOfBirth: user?.dateOfBirth || "",
        telegram:  user?.telegram  || "",
      });
    }
  }, [user, editMode]);

  const kycChip = {
    not_started: { label: "Не пройден", color: "red", icon: <AlertCircle size={13} /> },
    pending:     { label: "Проверка...", color: "gold", icon: <Shield size={13} /> },
    approved:    { label: "Подтверждён", color: "", icon: <CheckCircle2 size={13} /> },
    rejected:    { label: "Отклонён", color: "red", icon: <AlertCircle size={13} /> },
  }[user?.kycStatus || "not_started"];

  const handleAvatar = async (file) => {
    if (!file) return;
    if (file.size > 700_000) { useStore.getState().setToast("error", "Фото больше 700 КБ"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try { await updateProfile({ avatar: reader.result }); }
      catch (e) { useStore.getState().setToast("error", "Ошибка загрузки фото"); }
    };
    reader.readAsDataURL(file);
  };

  const saveEdit = async () => {
    try {
      await updateProfile(form);
      setEditMode(false);
    } catch (e) {
      useStore.getState().setToast("error", e.response?.data?.detail || "Ошибка");
    }
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <div className="row between">
          <h1 className="screen-title">👤 Профиль</h1>
          {!editMode ? (
            <button onClick={() => setEditMode(true)} data-testid="profile-edit-btn"
              style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "white", padding: "8px 14px", borderRadius: 10, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Edit3 size={14} /> Редактировать
            </button>
          ) : (
            <div className="row gap-sm">
              <button onClick={() => setEditMode(false)} data-testid="profile-cancel-btn"
                style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "white", padding: "8px 10px", borderRadius: 10, fontSize: 13, cursor: "pointer" }}>
                <X size={14} />
              </button>
              <button onClick={saveEdit} data-testid="profile-save-btn"
                style={{ background: "#E8A020", border: "none", color: "#0D1B4B", padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                <Save size={14} /> Сохранить
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {/* Avatar + Name card */}
        <div className="card mb-md" style={{ textAlign: "center", padding: 24 }}>
          <div style={{ position: "relative", width: 96, height: 96, margin: "0 auto 12px" }}>
            {user?.avatar ? (
              <img src={user.avatar} alt="avatar" data-testid="profile-avatar-img"
                style={{ width: 96, height: 96, borderRadius: 48, objectFit: "cover", border: "3px solid var(--gold)" }} />
            ) : (
              <div style={{ width: 96, height: 96, borderRadius: 48, background: "linear-gradient(135deg, #0D1B4B, #0E6B5E)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, fontWeight: 800 }}>
                {user?.firstName?.[0] || "U"}{user?.lastName?.[0] || ""}
              </div>
            )}
            <label htmlFor="avatar-upload" data-testid="profile-avatar-upload"
              style={{ position: "absolute", right: -4, bottom: -4, width: 32, height: 32, borderRadius: 16, background: "#E8A020", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "3px solid white" }}>
              <Camera size={14} color="#0D1B4B" />
              <input id="avatar-upload" type="file" accept="image/*" style={{ display: "none" }}
                onChange={(e) => handleAvatar(e.target.files?.[0])} />
            </label>
          </div>
          <div className="text-xl text-bold" data-testid="profile-fullname">
            {user?.firstName || "—"} {user?.lastName || ""}
          </div>
          <div className="text-sm text-muted">{user?.email}</div>
          <div className="mt-sm">
            <span className={`chip ${kycChip.color}`} data-testid="profile-kyc-chip">
              {kycChip.icon} KYC: {kycChip.label}
            </span>
          </div>
        </div>

        {/* Personal info */}
        <div className="text-bold mb-sm">Личные данные</div>
        <div className="card mb-md" style={{ padding: 0 }}>
          <InfoField icon={<User size={16} />} label="Имя" value={form.firstName}
            editable={editMode} onChange={(v) => setForm({ ...form, firstName: v })} testId="profile-field-firstname" />
          <InfoField icon={<User size={16} />} label="Фамилия" value={form.lastName}
            editable={editMode} onChange={(v) => setForm({ ...form, lastName: v })} testId="profile-field-lastname" />
          <InfoField icon={<Mail size={16} />} label="Email" value={user?.email} testId="profile-field-email" />
          <InfoField icon={<Phone size={16} />} label="Телефон" value={form.phone} placeholder="+7 701 234 5678"
            editable={editMode} onChange={(v) => setForm({ ...form, phone: v })} testId="profile-field-phone" />
          {editMode ? (
            <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <Globe size={14} /> Страна
              </div>
              <SearchableCountrySelect value={form.country} onChange={(c) => setForm({ ...form, country: c })}
                testId="mobile-profile-country" placeholder="Выберите страну" />
            </div>
          ) : (
            <InfoField icon={<Globe size={16} />} label="Страна"
              value={user?.country ? (
                <span data-testid="mobile-profile-country-display">
                  <span style={{ fontSize: 18, marginRight: 6 }}>{flagOf(user.country)}</span>
                  {findCountry(user.country).ru}
                  <span style={{ color: "var(--muted)", fontSize: 11, marginLeft: 6 }}>({user.country})</span>
                </span>
              ) : "—"}
              testId="profile-field-country" />
          )}
          <InfoField icon={<Calendar size={16} />} label="Дата рождения" value={form.dateOfBirth}
            editable={editMode} inputType="date"
            onChange={(v) => setForm({ ...form, dateOfBirth: v })} testId="profile-field-dob" />
          <InfoField icon={<MessageCircle size={16} />} label="Telegram" value={form.telegram} placeholder="@username"
            editable={editMode} onChange={(v) => setForm({ ...form, telegram: v })} testId="profile-field-telegram"
            last />
        </div>

        {/* KYC Section — locked until token launch on our blockchain */}
        <div className="card mb-md" data-testid="mobile-kyc-locked-notice"
          style={{ background: "linear-gradient(135deg, #FFF6DA, #FFE1B3)", border: "1.5px dashed rgba(232,160,32,0.45)" }}>
          <div className="row gap-sm mb-sm">
            <Lock size={18} color="#996700" />
            <span className="text-bold" style={{ color: "#996700" }}>KYC верификация закрыта</span>
          </div>
          <p className="text-sm" style={{ marginTop: 4, lineHeight: 1.6, color: "#664200" }}>
            Верификацию KYC нужно будет проходить после старта проекта и переноса всей базы на собственный блокчейн.
          </p>
        </div>

        {/* Security */}
        <div className="text-bold mt-md mb-sm">Безопасность</div>
        <ProfileRow icon={<Lock size={18} />} label="Сменить пароль"
          onClick={() => useStore.getState().setToast("info", "Доступно в следующей версии")} />
        <ProfileRow icon={<Shield size={18} />} label="Двухфакторная (2FA)"
          value={<span className="chip">Скоро</span>}
          onClick={() => useStore.getState().setToast("info", "2FA через Google Authenticator — Q2 2026")} />
        <ProfileRow icon={<Bell size={18} />} label="Уведомления"
          onClick={() => useStore.getState().setToast("info", "Push + Email + Telegram — настройки скоро")} />

        {/* Services */}
        <div className="text-bold mt-md mb-sm">Сервисы</div>
        <ProfileRow icon={<CreditCard size={18} />} label="Virtual Visa Card" onClick={() => onNav("card")} />
        <ProfileRow icon={<ShoppingBag size={18} />} label="Marketplace" onClick={() => onNav("marketplace")} />
        <ProfileRow icon={<ArrowLeftRight size={18} />} label="P2P Exchange" onClick={() => onNav("p2p")} />
        <ProfileRow icon={<Bot size={18} />} label="AI Assistant" onClick={() => onNav("ai")} />
        <ProfileRow icon={<Users size={18} />} label="Рефералы" onClick={() => onNav("referral")} />
        <ProfileRow icon={<Sparkles size={18} />} label="Magic 44 AirDrop" onClick={() => onNav("magic44")} />

        {/* Info */}
        <div className="text-bold mt-md mb-sm">Информация</div>
        <ProfileRow icon={<FileText size={18} />} label="Whitepaper"
          onClick={() => window.open("/api/investor/file/documents/02_TOKENOMICS.pdf", "_blank")} />
        <ProfileRow icon={<PiggyBank size={18} />} label="Bear Charity Mission"
          onClick={() => useStore.getState().setToast("info", "1% всех комиссий → на благотворительность")} />
        <ProfileRow icon="🛡️" label="Terms of Service" />
        <ProfileRow icon="🔒" label="Privacy Policy" />

        <button className="btn btn-outline btn-full mt-lg" onClick={logout} data-testid="logout-btn"
          style={{ color: "#EF4444", borderColor: "rgba(239,68,68,0.3)" }}>
          <LogOut size={16} /> Выйти
        </button>
        <p className="text-center text-hint text-sm mt-md">TGC App v1.1 DEMO · userId: {user?.userId?.slice(0, 12)}...</p>
      </div>

      {kycOpen && <KYCModal onClose={() => setKycOpen(false)} />}
    </div>
  );
}

function InfoField({ icon, label, value, editable, onChange, testId, placeholder, inputType, isSelect, options, last }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
      borderBottom: last ? "none" : "1px solid var(--border)" }}>
      <div style={{ color: "var(--teal)", flex: "0 0 auto" }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-hint text-sm" style={{ fontSize: 11 }}>{label}</div>
        {editable && onChange ? (
          isSelect ? (
            <select className="input" style={{ marginTop: 4 }} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
              {options.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
            </select>
          ) : (
            <input className="input" style={{ marginTop: 4 }} type={inputType || "text"}
              value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
          )
        ) : (
          <div className="text-bold" data-testid={testId} style={{ fontSize: 14, color: "var(--navy)", wordBreak: "break-word" }}>
            {value || <span className="text-hint" style={{ fontWeight: 400 }}>—</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileRow({ icon, label, onClick, value }) {
  return (
    <div className="card mb-sm" onClick={onClick} style={{ cursor: onClick ? "pointer" : "default", padding: "12px 16px" }}>
      <div className="row gap-sm">
        {typeof icon === "string" ? <span style={{ fontSize: 18 }}>{icon}</span> : <div style={{ color: "var(--teal)" }}>{icon}</div>}
        <span className="flex-1">{label}</span>
        {value || <ChevronRight size={18} color="#9CA3AF" />}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   KYC MODAL — document upload + auto-approve DEMO
═════════════════════════════════════════════════════════════════ */
function KYCModal({ onClose }) {
  const { submitKYC } = useStore();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    documentType: "passport", documentNumber: "", addressLine: "", city: "", postalCode: "",
    passportFront: null, passportBack: null, selfie: null,
  });

  const upload = (field, file) => {
    if (!file) return;
    if (file.size > 700_000) { useStore.getState().setToast("error", "Фото больше 700 КБ"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, [field]: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setLoading(true);
    try {
      await submitKYC(form);
      onClose();
    } catch (e) {
      useStore.getState().setToast("error", e.response?.data?.detail || "Ошибка");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 500, display: "flex", alignItems: "flex-end" }}>
      <div style={{ background: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 430, margin: "0 auto", maxHeight: "92vh", overflowY: "auto" }}>
        <div className="row between mb-md">
          <div>
            <h3 style={{ margin: 0, color: "var(--navy)" }}>🆔 Верификация личности</h3>
            <div className="text-hint text-sm">Шаг {step} из 3</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }} data-testid="kyc-close">
            <X size={24} />
          </button>
        </div>

        {/* Progress */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {[1, 2, 3].map((s) => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: step >= s ? "var(--gold)" : "var(--border)" }} />
          ))}
        </div>

        {step === 1 && (
          <>
            <div className="text-bold mb-sm">Тип документа</div>
            <div className="grid-3 mb-md">
              {[
                ["passport", "📘", "Паспорт"],
                ["id_card", "🪪", "ID-карта"],
                ["driver_license", "🚗", "Вод. права"],
              ].map(([id, ic, lbl]) => (
                <button key={id} onClick={() => setForm({ ...form, documentType: id })}
                  style={{ padding: 14, borderRadius: 12, border: form.documentType === id ? "2px solid var(--gold)" : "1px solid var(--border)",
                    background: form.documentType === id ? "rgba(232,160,32,0.08)" : "white", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
                  data-testid={`kyc-doctype-${id}`}>
                  <div style={{ fontSize: 26 }}>{ic}</div>
                  <div style={{ fontSize: 11, fontWeight: 600 }}>{lbl}</div>
                </button>
              ))}
            </div>
            <label className="input-label">Номер документа</label>
            <input className="input mb-md" placeholder="N1234567" value={form.documentNumber}
              onChange={(e) => setForm({ ...form, documentNumber: e.target.value })} data-testid="kyc-docnumber" />
            <label className="input-label">Адрес проживания</label>
            <input className="input mb-sm" placeholder="ул. Абая 15, кв. 25" value={form.addressLine}
              onChange={(e) => setForm({ ...form, addressLine: e.target.value })} data-testid="kyc-address" />
            <div className="grid-2 mb-md">
              <input className="input" placeholder="Город" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="kyc-city" />
              <input className="input" placeholder="Индекс" value={form.postalCode}
                onChange={(e) => setForm({ ...form, postalCode: e.target.value })} data-testid="kyc-zip" />
            </div>
            <button className="btn btn-primary btn-full" disabled={!form.documentNumber || !form.addressLine}
              onClick={() => setStep(2)} data-testid="kyc-step1-next">
              Продолжить →
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="text-bold mb-sm">Фото документа</div>
            <p className="text-sm text-muted mb-md">Загрузи чёткие фото обеих сторон документа. Макс 700 КБ каждая.</p>
            <UploadSlot label="Лицевая сторона" value={form.passportFront}
              onChange={(f) => upload("passportFront", f)} testId="kyc-front" />
            <UploadSlot label="Обратная сторона" value={form.passportBack}
              onChange={(f) => upload("passportBack", f)} testId="kyc-back" />
            <div className="row gap-sm mt-md">
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setStep(1)}>← Назад</button>
              <button className="btn btn-primary" style={{ flex: 2 }} disabled={!form.passportFront}
                onClick={() => setStep(3)} data-testid="kyc-step2-next">Далее →</button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="text-bold mb-sm">Селфи с документом</div>
            <p className="text-sm text-muted mb-md">Держи документ рядом с лицом. Хорошее освещение, без очков.</p>
            <UploadSlot label="Селфи" value={form.selfie}
              onChange={(f) => upload("selfie", f)} testId="kyc-selfie" />
            <div className="card mt-md" style={{ background: "rgba(59,130,246,0.08)" }}>
              <div className="text-sm text-muted">
                🛡️ DEMO режим: документы не отправляются на реальный провайдер. Одобрение произойдёт автоматически через 3 секунды.
              </div>
            </div>
            <div className="row gap-sm mt-md">
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setStep(2)}>← Назад</button>
              <button className="btn btn-gold" style={{ flex: 2 }} disabled={!form.selfie || loading}
                onClick={submit} data-testid="kyc-submit">
                {loading ? "..." : "✓ Отправить на проверку"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UploadSlot({ label, value, onChange, testId }) {
  const inputRef = useRef();
  return (
    <div onClick={() => inputRef.current?.click()} data-testid={testId}
      style={{
        height: 120, borderRadius: 14, border: "2px dashed var(--border)",
        background: value ? "transparent" : "#F9FAFB", marginBottom: 10,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden"
      }}>
      {value ? (
        <>
          <img src={value} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", top: 6, right: 6, background: "rgba(16,185,129,0.9)", color: "white", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
            ✓ {label}
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", color: "var(--text-muted)" }}>
          <Upload size={28} />
          <div className="text-sm" style={{ marginTop: 4 }}>{label}</div>
          <div className="text-hint text-sm">Нажми чтобы загрузить</div>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => onChange(e.target.files?.[0])} />
    </div>
  );
}
